/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 379 lines
 * - Adding: ~8 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-12-07
 * Modified By:  Cursor
 *
 * Description:
 * Standardize toast usage via centralized notifications helper.
 * Replace destructive archive confirmation with Modal dialog.
 * Enforce consistent UI messaging style and error sanitization.
 *
 * Related Screens / Modules:
 * - client/src/utils/notifications.ts
 * - client/src/features/setup/alerts/ProfilesList.tsx
 */

import React, { useState, useEffect } from "react";
import { Card, Table, Badge, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlay,
  faPause,
  faCopy,
  faTrash,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import {
  AlertProfile,
  listAlertProfiles,
  toggleAlertProfileStatus,
  deleteAlertProfile,
} from "../../../services/alertProfilesService";
import { notify } from "../../../utils/notifications";
import { formatShortDate } from "../../../utils/dateFormat";
import { getUserProfile } from "../../../services/usersService";
import { User } from "../../../interfaces/User";

interface ProfilesListProps {
  onEdit: (profile: AlertProfile) => void;
  onDuplicate: (profile: AlertProfile) => void;
  refreshTrigger?: number;
}

const ProfilesList: React.FC<ProfilesListProps> = ({ onEdit, onDuplicate, refreshTrigger = 0 }) => {
  const [profiles, setProfiles] = useState<AlertProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  // [2025-12-07] ui-standardization: Added delete confirmation modal state
  const [deleteConfirmProfile, setDeleteConfirmProfile] = useState<AlertProfile | null>(null);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listAlertProfiles();
      if (response.success) {
        setProfiles(response.data);
      } else {
        setError("Failed to load alert profiles");
      }
    } catch (err) {
      setError("Error loading alert profiles");
      console.error("Error loading profiles:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user profile to get country
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getUserProfile();
        const userProfile = response.data as User;
        setCountry(userProfile?.country || null);
      } catch (error) {
        console.warn("Failed to fetch user profile for date formatting:", error);
        // Continue without country - will use browser default locale
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [refreshTrigger]);

  const handleToggleStatus = async (profile: AlertProfile) => {
    try {
      const response = await toggleAlertProfileStatus(profile._id);
      if (response.success) {
        setProfiles((prev) =>
          prev.map((p) => (p._id === profile._id ? { ...p, isActive: !p.isActive } : p)),
        );
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success(`Profile ${profile.isActive ? "paused" : "activated"} successfully`);
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, "We couldn't update the profile status. Please try again");
      }
    } catch (err) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(err, "We couldn't update the profile status. Please try again");
      console.error("Error toggling profile status:", err);
    }
  };

  // [2025-12-07] ui-standardization: Replaced direct delete with Modal confirmation
  const handleDeleteClick = (profile: AlertProfile) => {
    setDeleteConfirmProfile(profile);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmProfile(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmProfile) return;

    try {
      const response = await deleteAlertProfile(deleteConfirmProfile._id);
      if (response.success) {
        setProfiles((prev) => prev.filter((p) => p._id !== deleteConfirmProfile._id));
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Profile deleted successfully");
        setDeleteConfirmProfile(null);
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, "We couldn't delete this profile. Please try again");
      }
    } catch (err) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(err, "We couldn't delete this profile. Please try again");
      console.error("Error deleting profile:", err);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Never";
    return formatShortDate(date, country, false);
  };

  const getModeBadge = (mode: "smart") => {
    return (
      <Badge className="badge-smart">
        Smart
      </Badge>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge className={isActive ? "badge-active" : "badge-paused"}>
        {isActive ? "Active" : "Paused"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading alert profiles...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">
            <Alert.Heading>Error Loading Profiles</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={loadProfiles}>
              Try Again
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (profiles.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <div className="mb-3">
            <FontAwesomeIcon icon={faEyeSlash} className="text-color-grey-1 icon-empty-state" />
          </div>
          <h5 className="text-muted">No Alert Profiles Found</h5>
          <p className="text-muted">
            You don't have any alert profiles yet. Choose one of the options above to get started.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="profiles-list-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-color-black fw-600">Your Alert Profiles</h5>
        <Badge bg="secondary">
          {profiles.length} profile{profiles.length !== 1 ? "s" : ""}
        </Badge>
      </Card.Header>
      <Card.Body className="p-0">
        {/* Desktop Table View */}
        <div className="d-none d-lg-block">
          <Table hover className="mb-0">
            <thead className="bg-light-bg">
              <tr>
                <th className="border-0 py-3 px-4 fw-600">Name</th>
                <th className="border-0 py-3 px-4 fw-600">Mode</th>
                <th className="border-0 py-3 px-4 fw-600">Status</th>
                <th className="border-0 py-3 px-4 fw-600">Est./Day</th>
                <th className="border-0 py-3 px-4 fw-600">Last Run</th>
                <th className="border-0 py-3 px-4 fw-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile._id} className="profiles-list-row">
                  <td className="py-3 px-4">
                    <div>
                      <div className="fw-semibold text-color-black">{profile.name}</div>
                      <small className="text-muted">Created {formatDate(profile.createdAt)}</small>
                    </div>
                  </td>
                  <td className="py-3 px-4">{getModeBadge(profile.mode)}</td>
                  <td className="py-3 px-4">{getStatusBadge(profile.isActive)}</td>
                  <td className="py-3 px-4">
                    <span className="text-muted">{profile.stats?.estPerDay || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-muted">{formatDate(profile.stats?.lastRunAt)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onEdit(profile)}
                        title="Edit Profile"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleToggleStatus(profile)}
                        title={profile.isActive ? "Pause Profile" : "Activate Profile"}
                      >
                        <FontAwesomeIcon icon={profile.isActive ? faPause : faPlay} />
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => onDuplicate(profile)}
                        title="Duplicate Profile"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(profile)}
                        title="Delete Profile"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Mobile Card View */}
        <div className="d-lg-none">
          {profiles.map((profile) => (
            <div key={profile._id} className="p-3 profiles-list-row">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="flex-grow-1">
                  <h6 className="mb-1 text-color-black fw-600">{profile.name}</h6>
                  <div className="d-flex gap-2 mb-2">
                    {getModeBadge(profile.mode)}
                    {getStatusBadge(profile.isActive)}
                  </div>
                </div>
              </div>

              <div className="row text-muted small mb-3">
                <div className="col-6">
                  <div>Est./Day: {profile.stats?.estPerDay || 0}</div>
                </div>
                <div className="col-6">
                  <div>Last Run: {formatDate(profile.stats?.lastRunAt)}</div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onEdit(profile)}
                  className="flex-fill"
                >
                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                  Edit
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleToggleStatus(profile)}
                  className="flex-fill"
                >
                  <FontAwesomeIcon icon={profile.isActive ? faPause : faPlay} className="me-1" />
                  {profile.isActive ? "Pause" : "Activate"}
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => onDuplicate(profile)}
                  className="flex-fill"
                >
                  <FontAwesomeIcon icon={faCopy} className="me-1" />
                  Duplicate
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteClick(profile)}
                  className="flex-fill"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card.Body>

      {/* Delete Confirmation Modal */}
      {/* [2025-12-07] ui-standardization: Replaced toast-based confirmation with Modal */}
      <Modal show={!!deleteConfirmProfile} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete "{deleteConfirmProfile?.name || "this profile"}"? This
            action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ProfilesList;
